<template>
  <q-page padding>
    <!-- Modern Header -->
    <q-card class="page-header q-mb-md">
      <q-card-section class="header-content">
        <div class="title-section">
          <q-avatar size="56px" color="primary" text-color="white" class="q-mr-md">
            <q-icon name="settings" size="32px" />
          </q-avatar>
          <div>
            <div class="text-h4">Configuraciones</div>
            <div class="text-subtitle2 text-white">Gestión de parámetros del sistema</div>
          </div>
        </div>
        <q-badge color="white" text-color="primary" class="q-px-md q-py-sm">
          <q-icon name="tune" size="18px" class="q-mr-xs" />
          Parámetros
        </q-badge>
      </q-card-section>
    </q-card>

    <!-- Content Card -->
    <q-card class="config-container">
      <q-card-section>
        <q-banner rounded class="bg-grey-2 text-grey-9 q-mb-md">
          <template v-slot:avatar>
            <q-icon name="info" color="primary" />
          </template>
          Los parámetros deben ser creados en Protheus para luego poder ser editados desde aquí.
        </q-banner>

        <q-list separator padding>
          <template v-for="(param, key) in parametros" :key="param">
            <q-item tag="label" class="q-py-md">
              <q-item-section>
                <q-item-label class="text-subtitle1 text-weight-medium">{{ param.titulo }}</q-item-label>
                <q-item-label caption class="q-mb-xs">{{ param.hint }}</q-item-label>
                <q-item-label>
                  <q-chip square size="sm" color="grey-3" text-color="grey-8" class="q-ma-none">
                    {{ key }}
                  </q-chip>
                </q-item-label>
              </q-item-section>
              <q-item-section side top class="config-input-section">
                <component :disable="loading" :is="componentes[param.component]" v-model="param.value"
                  @update:modelValue="valueUpdated(key)" outlined dense>
                </component>
              </q-item-section>
            </q-item>
          </template>
        </q-list>
      </q-card-section>
      <q-inner-loading :showing="dataLoading">
        <q-spinner-hourglass size="50px" color="primary" />
      </q-inner-loading>
    </q-card>
  </q-page>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import ConfigService from '@/services/ConfigService';
import GltnSelectListaPrecio from '@/components/form/GltnSelectListaPrecio.vue';
import GltnInput from '@/components/form/GltnInput.vue';
import { useQuasar } from 'quasar';

const $q = useQuasar();
const parametros = ref([]);
const loading = ref(false);
const dataLoading = ref(false);

const componentes = {
  'GltnSelectListaPrecio': GltnSelectListaPrecio,
  'GltnInput': GltnInput
}

onMounted(async () => {
  await getParametros();
});

const getParametros = async () => {
  dataLoading.value = true;
  parametros.value = await ConfigService.getParametros();
  dataLoading.value = false;
}

const valueUpdated = async (key) => {
  loading.value = true;
  try {
    await ConfigService.setVariable(key, parametros.value[key].value);
    $q.notify({
      color: 'positive',
      message: 'Guardado exitoso',
      icon: 'check'
    })
  } catch (error) {
    $q.notify({
      color: 'negative',
      message: 'Error al guardar',
      icon: 'error'
    })
  } finally {
    loading.value = false;
  }

}

</script>

<style lang="scss" scoped>
@use 'sass:color';

// Modern Header
.page-header {
  background: linear-gradient(135deg, #5e35b1 0%, #4527a0 100%);
  padding: 32px;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(94, 53, 177, 0.2);
  animation: fadeIn 0.5s ease-in-out;

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;

    .title-section {
      display: flex;
      align-items: center;
      color: white;

      .q-avatar {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      }

      .q-icon {
        opacity: 0.95;
      }

      .text-h4 {
        font-weight: 600;
        margin-bottom: 4px;
      }

      .text-subtitle2 {
        opacity: 0.9;
      }
    }

    .q-badge {
      font-size: 14px;
      padding: 8px 16px;
      border-radius: 20px;
      font-weight: 500;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    }
  }
}

// Content Card
.config-container {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  animation: fadeIn 0.6s ease-in-out;

  .config-input-section {
    min-width: 300px;
  }
}

// Animations
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// Responsive
@media (max-width: 768px) {
  .page-header {
    .header-content {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  .config-container {
    .config-input-section {
      min-width: 100%;
      padding-left: 0 !important;
      margin-top: 8px;
    }

    :deep(.q-item) {
      flex-direction: column;
      align-items: stretch;
    }
  }
}
</style>
